/**
 * Screen-to-screen slide for Variant B (HL-12034 1.12).
 *
 * QuestionnaireVariantB derives a transition from each change of the screen
 * stack and provides it here; QuestionFrame keys its content region on
 * `key` so the new screen remounts and plays the enter animation. The back
 * arrow / progress row and the fixed mobile CTA sit outside that region and
 * stay put.
 *
 *   push, regular page   → "left"  (enters from the right)
 *   push, follow-up page → "up"    (enters from the bottom)
 *   pop                  → the reverse of the screen being left
 *   stack replaced (resume / post-login rebuild) → no animation
 */

import React, { createContext, useContext, useRef } from "react";

export type ScreenTransitionDirection = "left" | "right" | "up" | "down";

export interface ScreenTransition {
  key: number;
  direction: ScreenTransitionDirection | null;
}

const ScreenTransitionContext = createContext<ScreenTransition>({ key: 0, direction: null });

export const ScreenTransitionProvider = ScreenTransitionContext.Provider;

export const useScreenTransition = () => useContext(ScreenTransitionContext);

type StackEntry = { type: string; page?: { subIdx: number | null } };

const isFollowUp = (screen: StackEntry | undefined) =>
  screen?.type === "page" && screen.page?.subIdx !== null && screen.page?.subIdx !== undefined;

/**
 * Compares the stack with the one from the previous render. Refs only, so a
 * repeated render (StrictMode) with the same stack is a no-op. While the
 * intro is up the direction is cleared, so the first question does not replay
 * a stale slide when it mounts after Start.
 */
export function useScreenTransitionFromStack(stack: StackEntry[], showIntro: boolean): ScreenTransition {
  const prevStackRef = useRef(stack);
  const transitionRef = useRef<ScreenTransition>({ key: 0, direction: null });

  const prev = prevStackRef.current;
  if (prev !== stack) {
    prevStackRef.current = stack;
    let direction: ScreenTransitionDirection | null = null;
    if (stack.length === prev.length + 1 && stack[stack.length - 2] === prev[prev.length - 1]) {
      direction = isFollowUp(stack[stack.length - 1]) ? "up" : "left";
    } else if (stack.length < prev.length && stack[stack.length - 1] === prev[stack.length - 1]) {
      direction = isFollowUp(prev[prev.length - 1]) ? "down" : "right";
    }
    if (direction) transitionRef.current = { key: transitionRef.current.key + 1, direction };
  }

  if (showIntro && transitionRef.current.direction) {
    transitionRef.current = { key: transitionRef.current.key, direction: null };
  }

  return transitionRef.current;
}

// Offset + fade rather than a full-width slide: the old screen unmounts at
// once, so a 100% slide would sweep in over an empty page.
const DURATION_MS = 300;
const OFFSET = "32px";

const ANIMATION_CLASS: Record<ScreenTransitionDirection, string> = {
  left: "wl2-screen-enter-left",
  right: "wl2-screen-enter-right",
  up: "wl2-screen-enter-up",
  down: "wl2-screen-enter-down",
};

export const screenTransitionClass = (direction: ScreenTransitionDirection | null) =>
  direction ? ANIMATION_CLASS[direction] : "";

/**
 * `backwards` fill (not `both`): no transform is left on the wrapper once the
 * animation ends, so it never becomes the containing block of a
 * position:fixed descendant.
 */
export const ScreenTransitionStyles: React.FC = () => (
  <style>{`
    @keyframes wl2-screen-from-right { from { opacity: 0; transform: translateX(${OFFSET}); } to { opacity: 1; transform: none; } }
    @keyframes wl2-screen-from-left { from { opacity: 0; transform: translateX(-${OFFSET}); } to { opacity: 1; transform: none; } }
    @keyframes wl2-screen-from-bottom { from { opacity: 0; transform: translateY(${OFFSET}); } to { opacity: 1; transform: none; } }
    @keyframes wl2-screen-from-top { from { opacity: 0; transform: translateY(-${OFFSET}); } to { opacity: 1; transform: none; } }
    .wl2-screen-enter-left { animation: wl2-screen-from-right ${DURATION_MS}ms ease-out backwards; }
    .wl2-screen-enter-right { animation: wl2-screen-from-left ${DURATION_MS}ms ease-out backwards; }
    .wl2-screen-enter-up { animation: wl2-screen-from-bottom ${DURATION_MS}ms ease-out backwards; }
    .wl2-screen-enter-down { animation: wl2-screen-from-top ${DURATION_MS}ms ease-out backwards; }
    @media (prefers-reduced-motion: reduce) {
      .wl2-screen-enter-left, .wl2-screen-enter-right, .wl2-screen-enter-up, .wl2-screen-enter-down { animation: none; }
    }
  `}</style>
);
